// @ts-nocheck

import { useState, useEffect, useCallback, useMemo } from 'react';
import { FilterState, Property } from '@/types/property';
import { areaAPI, propertyAPI, propertyTypeAPI } from '@/lib/api';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { useDebounce } from './useDebounce';
import { format } from 'date-fns';

export function usePropertyListing() {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    // Initialize state from URL params
    const [filters, setFilters] = useState<FilterState>(() => {
        const area = searchParams?.get('area') || '';
        const search = searchParams?.get('search') || '';
        const type = searchParams?.get('type') || '';
        const minPrice = searchParams?.get('minPrice') ? Number(searchParams.get('minPrice')) : '';
        const maxPrice = searchParams?.get('maxPrice') ? Number(searchParams.get('maxPrice')) : '';
        const bedrooms = searchParams?.get('bedrooms') || undefined;
        const bathrooms = searchParams?.get('bathrooms') || undefined;
        const guests = searchParams?.get('guests') ? Number(searchParams.get('guests')) : 0;
        const amenities = searchParams?.get('amenities') ? searchParams.get('amenities')!.split(',') : [];

        // Handle dates
        const checkInParam = searchParams?.get('checkIn');
        const checkOutParam = searchParams?.get('checkOut');

        let checkIn: Date | undefined;
        let checkOut: Date | undefined;

        if (checkInParam) {
            const parsed = new Date(checkInParam);
            if (!isNaN(parsed.getTime())) checkIn = parsed;
        }

        if (checkOutParam) {
            const parsed = new Date(checkOutParam);
            if (!isNaN(parsed.getTime())) checkOut = parsed;
        }

        return {
            search,
            checkin: checkIn,
            checkout: checkOut,
            area,
            type,
            minPrice,
            maxPrice,
            amenities,
            bedrooms,
            bathrooms,
            guests
        };
    });

    const [propertyTypesList, setPropertyTypesList] = useState<any[]>([]);
    const [areaList, setAreaList] = useState<any[]>([]);
    const [properties, setProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [amenitiesList, setAmenitiesList] = useState<any[]>([]);

    const debouncedSearch = useDebounce(filters.search, 500);
    const debouncedMinPrice = useDebounce(filters.minPrice, 500);
    const debouncedMaxPrice = useDebounce(filters.maxPrice, 500);

    // Sync state to URL
    useEffect(() => {
        const params = new URLSearchParams();

        if (debouncedSearch) params.set('search', debouncedSearch);
        if (filters.area) params.set('area', filters.area);
        if (filters.type && filters.type !== 'All types') params.set('type', filters.type);
        if (debouncedMinPrice) params.set('minPrice', String(debouncedMinPrice));
        if (debouncedMaxPrice) params.set('maxPrice', String(debouncedMaxPrice));
        if (filters.bedrooms && filters.bedrooms !== 'any') params.set('bedrooms', filters.bedrooms);
        if (filters.bathrooms && filters.bathrooms !== 'any') params.set('bathrooms', filters.bathrooms);
        if (filters.guests && filters.guests > 0) params.set('guests', String(filters.guests));
        if (filters.amenities && filters.amenities.length > 0) params.set('amenities', filters.amenities.join(','));

        if (filters.checkin) params.set('checkIn', format(filters.checkin, 'yyyy-MM-dd'));
        if (filters.checkout) params.set('checkOut', format(filters.checkout, 'yyyy-MM-dd'));

        const queryString = params.toString();
        // Only push if different to avoid redundant history entries/reflashes? 
        // Simple replace for now to update URL without adding history for every keystroke if debounced
        // Actually, for debounced values, push might be okay. But let's use replace to be safe on typing.
        // Wait, for filters we want history? Maybe.

        router.replace(`${pathname}?${queryString}`, { scroll: false });

    }, [
        debouncedSearch,
        filters.area,
        filters.type,
        debouncedMinPrice,
        debouncedMaxPrice,
        filters.bedrooms,
        filters.bathrooms,
        filters.guests,
        filters.amenities,
        filters.checkin,
        filters.checkout,
        pathname,
        router
    ]);

    // Fetch properties based on URL params (source of truth)
    // Actually, we can fetch based on filters state since it's synced via effect?
    // Or prefer fetching based on `searchParams` directly to be truly URL-driven?
    // Let's stick to using `filters` state coupled with the debounced values for fetching to maintain existing logic,
    // but the URL update effect runs parallel.

    // Initial data fetch
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [areasResp, typesResp] = await Promise.all([
                    areaAPI.getFrontendList(),
                    propertyTypeAPI.getAllWeb('property_type')
                ]);

                const areasRaw = (areasResp as any)?.data;
                const areas = (areasRaw?.data ?? areasRaw?.items ?? areasRaw?.results ?? areasRaw) as any[];
                setAreaList(Array.isArray(areas) ? areas : []);

                const typesRaw = (typesResp as any)?.data;
                const types = (typesRaw?.data ?? typesRaw?.items ?? typesRaw?.results ?? typesRaw) as any[];
                setPropertyTypesList(Array.isArray(types) ? types : []);

                const amenitiesResp = await propertyTypeAPI.getAllWeb('amenity');
                const amenitiesRaw = (amenitiesResp as any)?.data;
                const amenities = (amenitiesRaw?.data ?? amenitiesRaw?.items ?? amenitiesRaw?.results ?? amenitiesRaw) as any[];
                setAmenitiesList(Array.isArray(amenities) ? amenities : []);
            } catch (err) {
                console.error('Error fetching initial data:', err);
            }
        };

        fetchInitialData();
    }, []);

    // Fetch properties when meaningful filters change
    const fetchProperties = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const apiParams: any = {};

            if (debouncedSearch) apiParams.search = debouncedSearch;
            if (filters.area) apiParams.area = filters.area;
            if (filters.type && filters.type !== 'All types') {
                apiParams.type = filters.type;
            }
            if (filters.bedrooms && filters.bedrooms !== 'any') apiParams.bedrooms = filters.bedrooms;
            if (filters.bathrooms && filters.bathrooms !== 'any' && filters.bathrooms !== 'none') apiParams.bathrooms = filters.bathrooms;
            if (filters.amenities && filters.amenities.length > 0) {
                apiParams.amenities = filters.amenities.join(',');
            }
            if (filters.guests && Number(filters.guests) > 0) {
                apiParams.guests = Number(filters.guests);
            }
            if (debouncedMinPrice !== '' && debouncedMinPrice !== undefined) apiParams.minPrice = Number(debouncedMinPrice);
            if (debouncedMaxPrice !== '' && debouncedMaxPrice !== undefined) apiParams.maxPrice = Number(debouncedMaxPrice);

            if (filters.checkin) apiParams.checkin = format(filters.checkin, 'yyyy-MM-dd');
            if (filters.checkout) apiParams.checkout = format(filters.checkout, 'yyyy-MM-dd');

            // console.log('Fetching properties with params:', apiParams);

            const resp = await propertyAPI.getAllWeb(apiParams);
            const raw = (resp as any)?.data;
            const list = Array.isArray(raw) ? raw : ((raw?.data ?? raw?.items ?? raw?.results ?? raw) as any[]);

            setProperties(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Error fetching properties:', err);
            setError('Failed to load properties. Please try again.');
            setProperties([]);
        } finally {
            setLoading(false);
        }
    }, [
        debouncedSearch,
        debouncedMinPrice,
        debouncedMaxPrice,
        filters.area,
        filters.type,
        filters.bedrooms,
        filters.bathrooms,
        filters.amenities,
        filters.guests,
        filters.checkin,
        filters.checkout
    ]);

    useEffect(() => {
        fetchProperties();
    }, [fetchProperties]);

    // Listen for URL changes (popstate) to sync back to state?
    // Next.js `useSearchParams` updates on navigation. 
    // We need to update state when searchParams change externally (e.g. back button).
    // HOWEVER, if we just set state on mount and then push updates, back button might not update state 
    // because `useState` initializer only runs once.
    // We need an effect that watches `searchParams` and updates `filters` state if it differs?
    // But touching `filters` triggers the other effect which pushes to URL -> loop?
    // We should allow the URL to drive the state or rely on the fact that `router.push` causes a re-render/navigation?
    // Actually, simply relying on `filters` state as the source of truth for the UI and pushing to URL is one way (controlled).
    // But for "Back" support, we need to ingest changes from URL.

    useEffect(() => {
        // Construct a filter object from current params
        const newFilters: FilterState = {
            search: searchParams?.get('search') || '',
            area: searchParams?.get('area') || '',
            type: searchParams?.get('type') || '',
            minPrice: searchParams?.get('minPrice') ? Number(searchParams.get('minPrice')) : '',
            maxPrice: searchParams?.get('maxPrice') ? Number(searchParams.get('maxPrice')) : '',
            bedrooms: searchParams?.get('bedrooms') || undefined,
            bathrooms: searchParams?.get('bathrooms') || undefined,
            guests: searchParams?.get('guests') ? Number(searchParams.get('guests')) : 0,
            amenities: searchParams?.get('amenities') ? searchParams.get('amenities')!.split(',') : [],
            checkin: undefined,
            checkout: undefined
        };

        const ci = searchParams?.get('checkIn');
        if (ci) {
            const d = new Date(ci);
            if (!isNaN(d.getTime())) {
                newFilters.checkin = d;
            }
        }
        const co = searchParams?.get('checkOut');
        if (co) {
            const d = new Date(co);
            if (!isNaN(d.getTime())) {
                newFilters.checkout = d;
            }
        }

        // We only want to setFilters if they are materially different to avoid loops?
        // Or deeper comparison. 
        // For simple implementation, let's assume if searchParams changed, we should reflect it.
        // But the `filters` state update triggers the URL push.
        // If the URL push matches the current params, hopefully `useSearchParams` won't trigger this again?
        // Let's try deep comparison or simply blindly setting it might cause a loop if we are not careful.
        // Actually, if we update state -> triggers fetch -> triggers URL update.
        // If we update URL -> triggers this effect -> updates state.

        // Let's trust that we only update state if we navigated? 
        // Actually, the best pattern for "URL as source of truth" is to NOT have local state for filters unless debouncing.
        // But `FilterBar` expects `filters` object.
        // Let's stick to: Local state drives URL. URL changes (via back button) drive local state.

        setFilters(prev => {
            // Simplified check to avoid loop, or strict equality on primitives?
            // Since we're pushing to URL based on 'filters', if URL matches 'filters', no op.
            // If URL changed (e.g. Back button), 'filters' will be stale relative to URL.
            // So we update 'filters'.
            // But we need to avoid the case where 'filters' update -> URL update -> searchParams change -> 'filters' update...

            // To break the loop:
            // The URL update effect depends on `filters`.
            // This effect depends on `searchParams`.

            // We can compare `newFilters` against `prev`.
            // JSON stringify is cheap enough for this size.
            const currentAsString = JSON.stringify({
                ...prev,
                // normalize dates for comparison?
                checkin: prev.checkin?.toISOString().split('T')[0],
                checkout: prev.checkout?.toISOString().split('T')[0]
            });
            const newAsString = JSON.stringify({
                ...newFilters,
                checkin: newFilters.checkin?.toISOString().split('T')[0],
                checkout: newFilters.checkout?.toISOString().split('T')[0]
            });

            if (currentAsString === newAsString) return prev;

            return newFilters;
        });

    }, [searchParams]); // Dependency on searchParams

    return {
        filters,
        setFilters,
        properties,
        propertyTypesList,
        areaList,
        loading,
        error,
        amenitiesList
    };
}
